import { useCallback, useEffect, useMemo, useState } from "react";
import { assetClassOf } from "../lib/assetClass";
import type {
  ApiClient,
  CryptoPaperEngineStatusWire,
  CryptoPaperEngineRunResponseWire,
  CryptoSignalsGenerateResponseWire,
  CryptoPaperTradeHistoryRowWire,
  CryptoWatchlistAnalysisRowWire,
} from "../api/client";
import type { CryptoValidationRun } from "../api/dataSource";
import type { Account, EventRow, Pending } from "../types";

type Props = {
  apiClient: ApiClient;
  accounts: Account[];
  pending: Pending[];
  recentEvents: EventRow[];
  cryptoValidationRuns: CryptoValidationRun[];
  listCryptoValidationRuns: (limit?: number) => Promise<CryptoValidationRun[]>;
  usingFixtures: boolean;
  onOpenApproval: (approvalId: string) => void;
  onRefresh?: () => Promise<void>;
};

// The old local isCryptoSymbol suffix heuristic moved to
// lib/assetClass.ts (looksLikeCryptoSymbol) and now only serves as
// the fallback inside assetClassOf for rows predating the
// server-resolved assetClass field.

const formatMoney = (value: string | number) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n);
};

const splitPair = (symbol: string) => {
  const [base, quote] = symbol.split("-");
  return { base: base || symbol, quote: quote || "USD" };
};

const formatPct = (value: string | number | null) => {
  if (value === null) return "n/a";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `${n.toFixed(2)}%`;
};

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatR = (value: string | number | null) => {
  if (value === null) return "n/a";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `${n.toFixed(2)}R`;
};

const shortDate = (value: string | null) => {
  if (!value) return "-";
  return value.slice(0, 10);
};

const checkLabel = (value: boolean | null) => {
  if (value === true) return "pass";
  if (value === false) return "fail";
  return "n/a";
};

const checkTone = (value: boolean | null): "pos" | "neg" | undefined => {
  if (value === true) return "pos";
  if (value === false) return "neg";
  return undefined;
};

const formatParams = (params: Record<string, unknown>) => {
  const entries = Object.entries(params);
  if (entries.length === 0) return "default";
  return entries
    .slice(0, 2)
    .map(([key, value]) => `${key}=${String(value)}`)
    .join(", ");
};

const fixtureCryptoWatchlistRows = (): CryptoWatchlistAnalysisRowWire[] => [
  {
    symbol: "BTC-USD",
    venue: "Alpaca",
    instrument_type: "spot",
    last_price: "68000",
    trigger_price: "68100",
    distance_pct: "0.2",
    proximity: 0.9,
    plan: "breakout_v1",
    blocked_reason: null,
    depth_state: "ok",
    funding_rate: null,
    regime_label: "trending_bull",
    ai_confidence: null,
    det_score: 82,
    analysis: "Fixture-mode watchlist row for layout validation.",
    cited: "fixture",
    as_of: new Date().toISOString(),
  },
  {
    symbol: "ETH-USD",
    venue: "Alpaca",
    instrument_type: "spot",
    last_price: "3880",
    trigger_price: "3920",
    distance_pct: "1.0",
    proximity: 0.62,
    plan: "mean_reversion_v1",
    blocked_reason: null,
    depth_state: "watch",
    funding_rate: null,
    regime_label: "ranging",
    ai_confidence: null,
    det_score: 68,
    analysis: "Fixture-mode watchlist row for layout validation.",
    cited: "fixture",
    as_of: new Date().toISOString(),
  },
];

const fixtureCryptoEngineStatus = (): CryptoPaperEngineStatusWire => ({
  auto_trading_enabled: true,
  auto_approval_enabled: false,
  registered_crypto_paper_adapters: 1,
  max_open_trades: 2,
  entries_today: 1,
  max_daily_entries: 5,
  risk_per_trade_pct: "0.25",
  exit_intents_by_status: { open: 1 },
  last_run: {
    status: "ok",
    candidates_scored: 2,
    approvals_created: 1,
    orders_placed: 0,
  },
});

const CRYPTO_SIGNAL_RUN_STORAGE_KEY = "tradepro.crypto.signalWorkbench.lastRun";
const CRYPTO_SIGNAL_RUN_STORAGE_VERSION = 2;

type StoredCryptoSignalRun = {
  version: number;
  run: CryptoSignalsGenerateResponseWire;
};

const readStoredCryptoSignalRun =
  (): CryptoSignalsGenerateResponseWire | null => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(CRYPTO_SIGNAL_RUN_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<StoredCryptoSignalRun>;
      if (parsed.version !== CRYPTO_SIGNAL_RUN_STORAGE_VERSION) {
        window.localStorage.removeItem(CRYPTO_SIGNAL_RUN_STORAGE_KEY);
        return null;
      }
      const run = parsed.run as Partial<CryptoSignalsGenerateResponseWire>;
      if (
        typeof run.run_id !== "string" ||
        run.mode !== "queue_approvals" ||
        !Array.isArray(run.candidates)
      ) {
        return null;
      }
      return run as CryptoSignalsGenerateResponseWire;
    } catch {
      return null;
    }
  };

const storeCryptoSignalRun = (run: CryptoSignalsGenerateResponseWire) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CRYPTO_SIGNAL_RUN_STORAGE_KEY,
      JSON.stringify({
        version: CRYPTO_SIGNAL_RUN_STORAGE_VERSION,
        run,
      } satisfies StoredCryptoSignalRun),
    );
  } catch {
    // Storage can be disabled in private browsing; the server-side
    // approval queue remains the source of truth.
  }
};

export const CryptoScreen = ({
  apiClient,
  accounts,
  pending,
  recentEvents,
  cryptoValidationRuns,
  listCryptoValidationRuns,
  usingFixtures,
  onOpenApproval,
  onRefresh,
}: Props) => {
  const [watchlistRows, setWatchlistRows] = useState<
    CryptoWatchlistAnalysisRowWire[]
  >([]);
  const [watchlistStatus, setWatchlistStatus] = useState<
    "loading" | "live" | "unavailable"
  >("loading");
  const [watchlistError, setWatchlistError] = useState<string | null>(null);
  const [watchlistEmpty, setWatchlistEmpty] = useState(false);
  const [engineStatus, setEngineStatus] =
    useState<CryptoPaperEngineStatusWire | null>(null);
  const [tradeHistoryRows, setTradeHistoryRows] = useState<
    CryptoPaperTradeHistoryRowWire[]
  >([]);
  const [engineRun, setEngineRun] =
    useState<CryptoPaperEngineRunResponseWire | null>(null);
  const [engineRunError, setEngineRunError] = useState<string | null>(null);
  const [engineRunning, setEngineRunning] = useState(false);
  const [signalRun, setSignalRun] =
    useState<CryptoSignalsGenerateResponseWire | null>(
      readStoredCryptoSignalRun,
    );
  const [signalRunError, setSignalRunError] = useState<string | null>(null);
  const [signalRunning, setSignalRunning] = useState(false);
  const [validationRuns, setValidationRuns] =
    useState<CryptoValidationRun[]>(cryptoValidationRuns);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [selectedValidationId, setSelectedValidationId] = useState<string | null>(
    cryptoValidationRuns[0]?.id ?? null,
  );

  useEffect(() => {
    setValidationRuns((current) =>
      current.length > 0 ? current : cryptoValidationRuns,
    );
    setSelectedValidationId((current) =>
      current ?? cryptoValidationRuns[0]?.id ?? null,
    );
  }, [cryptoValidationRuns]);

  const refreshValidationRuns = useCallback(async () => {
    setValidationLoading(true);
    setValidationError(null);
    try {
      const runs = await listCryptoValidationRuns(25);
      setValidationRuns(runs);
      setSelectedValidationId((current) =>
        current && runs.some((run) => run.id === current)
          ? current
          : runs[0]?.id ?? null,
      );
    } catch (error) {
      setValidationError(
        error instanceof Error
          ? error.message
          : "Crypto validation runs unavailable",
      );
    } finally {
      setValidationLoading(false);
    }
  }, [listCryptoValidationRuns]);

  const refreshCryptoData = useCallback(async () => {
    if (usingFixtures) {
      const rows = fixtureCryptoWatchlistRows();
      setWatchlistRows(rows);
      setEngineStatus(fixtureCryptoEngineStatus());
      setTradeHistoryRows([]);
      setWatchlistStatus("live");
      setWatchlistError(null);
      setWatchlistEmpty(rows.length === 0);
      return;
    }
    const response = await apiClient.getCryptoWatchlistAnalysis();
    const status = await apiClient.getCryptoPaperEngineStatus();
    setWatchlistRows(response.rows);
    setEngineStatus(status);
    setWatchlistStatus("live");
    setWatchlistError(null);
    setWatchlistEmpty(response.rows.length === 0);
    try {
      const history = await apiClient.getCryptoPaperTradeHistory(25);
      setTradeHistoryRows(history.rows);
    } catch {
      setTradeHistoryRows([]);
    }
  }, [apiClient, usingFixtures]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        await refreshCryptoData();
        await refreshValidationRuns();
        if (cancelled) return;
      } catch (error) {
        if (cancelled) return;
        setWatchlistRows([]);
        setEngineStatus(null);
        setTradeHistoryRows([]);
        setWatchlistStatus("unavailable");
        setWatchlistEmpty(false);
        setWatchlistError(
          error instanceof Error
            ? error.message
            : "Crypto watchlist analysis unavailable",
        );
      }
    };

    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [refreshCryptoData, refreshValidationRuns]);

  const handleRunCryptoSignals = useCallback(async () => {
    setSignalRunning(true);
    setSignalRunError(null);
    try {
      if (usingFixtures) {
        const fixtureRun: CryptoSignalsGenerateResponseWire = {
          status: "ok",
          run_id: "fixture-crypto-signal-run",
          generated_at: new Date().toISOString(),
          universe: "fixture",
          mode: "queue_approvals",
          summary: {
            candidates_generated: 2,
            candidates_scored: 2,
            approvals_queued: 1,
            blocked: 0,
            watch_only: 1,
            auto_approval_confidence_threshold: 70,
          },
          candidates: [],
        };
        setSignalRun(fixtureRun);
        storeCryptoSignalRun(fixtureRun);
        await refreshCryptoData();
        return;
      }
      const result = await apiClient.generateCryptoSignals();
      setSignalRun(result);
      storeCryptoSignalRun(result);
      await refreshCryptoData();
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      setSignalRunError(
        error instanceof Error ? error.message : "Crypto signal run failed",
      );
    } finally {
      setSignalRunning(false);
    }
  }, [apiClient, onRefresh, refreshCryptoData, usingFixtures]);

  const handleRunAutoPaperEngine = useCallback(async () => {
    setEngineRunning(true);
    setEngineRun(null);
    setEngineRunError(null);
    try {
      if (usingFixtures) {
        setEngineRun({
          status: "ok",
          candidates_scored: 2,
          approvals_created: 1,
          orders_placed: 0,
        });
        await refreshCryptoData();
        return;
      }
      const result = await apiClient.runCryptoPaperEngine({ asyncRun: true });
      setEngineRun(result);
      await refreshCryptoData();
      for (const delay of [5_000, 15_000, 30_000, 60_000]) {
        window.setTimeout(() => void refreshCryptoData(), delay);
      }
    } catch (error) {
      setEngineRunError(
        error instanceof Error ? error.message : "Crypto auto engine run failed",
      );
    } finally {
      setEngineRunning(false);
    }
  }, [apiClient, refreshCryptoData, usingFixtures]);

  const cryptoPending = useMemo(
    // assetClass-first (Phase 9-UI); assetClassOf falls back to the
    // legacy symbol-suffix heuristic for rows predating the field.
    () => pending.filter((p) => assetClassOf(p) === "crypto"),
    [pending],
  );
  const displayedEngineRun =
    engineRun?.status === "accepted" && engineStatus?.last_run
      ? engineStatus.last_run
      : engineRun;

  const cryptoAccounts = useMemo(
    () =>
      accounts.filter((a) =>
        a.assetClass === "crypto" ||
        `${a.broker} ${a.label}`.toLowerCase().includes("crypto"),
      ),
    [accounts],
  );

  const cryptoEvents = useMemo(
    () =>
      recentEvents.filter((e) =>
        `${e.type} ${e.summary}`.toLowerCase().includes("crypto"),
      ),
    [recentEvents],
  );

  const cryptoEquity = cryptoAccounts.reduce(
    (sum, account) => sum + account.equity,
    0,
  );
  const selectedValidation =
    validationRuns.find((run) => run.id === selectedValidationId) ??
    validationRuns[0] ??
    null;
  const latestValidation = validationRuns[0] ?? null;

  return (
    <div data-screen-label="Crypto">
      <div className="tp-page-head">
        <h1>Crypto</h1>
        <div className="cx-clocks">
          <span>
            <span className="lbl">UTC</span>
            {new Date().toISOString().slice(11, 19)}
          </span>
        </div>
      </div>

      <div className="tp-acct-row" style={{ marginBottom: 14 }}>
        {cryptoAccounts.length === 0 ? (
          <div className="tp-acct-card trading-paper">
            <div className="head">
              <span className="tp-acct-tag trading-paper">Crypto paper</span>
              <span className="broker num">Alpaca Crypto · not wired</span>
            </div>
            <div className="equity num">$0.00</div>
            <div className="sub">
              Configure crypto credentials and paper-validation controls under
              Config → AI.
            </div>
          </div>
        ) : (
          cryptoAccounts.map((account) => (
            <div
              key={account.brokerAccountId}
              className={`tp-acct-card ${account.kind}`}
            >
              <div className="head">
                <span className={`tp-acct-tag ${account.kind}`}>
                  {account.label}
                </span>
                <span className="broker num">{account.broker}</span>
              </div>
              <div className="equity num">{formatMoney(account.equity)}</div>
              <div className="meta">
                <span className={account.todayPL >= 0 ? "pos" : "neg"}>
                  {formatMoney(account.todayPL)} 24h
                </span>
              </div>
              <div className="sub">
                {account.balanceSource === "asset_class_sleeve" ||
                account.balanceSource === "asset_class_exposure"
                  ? `crypto sleeve · broker ${formatMoney(account.brokerEquity ?? account.equity)}`
                  : `status ${account.status}`}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="tp-crypto-grid">
        <section className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Crypto day-trade envelope</span>
            <span className="cx-candle">
              <span className="dot" />
              00:00 UTC reset
            </span>
          </div>
          <div className="tp-tile-body">
            <div className="tp-crypto-metrics">
              <Metric label="Paper equity" value={formatMoney(cryptoEquity)} />
              <Metric
                label="Pending"
                value={String(cryptoPending.length)}
                tone={cryptoPending.length > 0 ? "warn" : undefined}
              />
              <Metric
                label="Venue"
                value={cryptoAccounts.length > 0 ? "Alpaca" : "Missing"}
                tone={cryptoAccounts.length > 0 ? "pos" : "warn"}
              />
              <Metric
                label="Auto paper"
                value={engineStatus?.auto_trading_enabled ? "On" : "Off"}
                tone={engineStatus?.auto_trading_enabled ? "pos" : "warn"}
              />
              <Metric
                label="Open cap"
                value={`${engineStatus?.max_open_trades ?? 2}`}
              />
            </div>
            <div className="cx-note">
              Setup, provider keys, push config, and 30-day paper-validation
              entry are managed from Config → AI.
            </div>
          </div>
        </section>

        <section className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Venue health</span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            {cryptoAccounts.length === 0 ? (
              <div className="cx-venue-row">
                <span className="name">AP</span>
                <span>No Alpaca crypto paper account registered</span>
                <span className="lat">n/a</span>
                <span className="bal">$0</span>
              </div>
            ) : (
              cryptoAccounts.map((account) => (
                // Key by the broker UUID — two paper accounts share the
                // legacy domain id ("paper") and would collide.
                <div className="cx-venue-row" key={account.brokerAccountId}>
                  <span className="name">AP</span>
                  <span>{account.label}</span>
                  <span className="lat">{account.status}</span>
                  <span className="bal">{formatMoney(account.equity)}</span>
                </div>
              ))
            )}
          </div>
        </section>

        <section
          className="tp-tile"
          style={{ gridColumn: "1 / -1" }}
        >
          <div className="tp-tile-head">
            <span className="label">Signal Workbench</span>
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {signalRun ? (
                <span style={{ fontSize: 11, color: "var(--tp-fg-3)" }}>
                  {signalRun.summary.candidates_generated} generated ·{" "}
                  {signalRun.summary.candidates_scored} scored ·{" "}
                  {signalRun.summary.approvals_queued} queued ·{" "}
                  {signalRun.summary.blocked} blocked ·{" "}
                  {signalRun.summary.watch_only} watch-only · min{" "}
                  {signalRun.summary.auto_approval_confidence_threshold} ·{" "}
                  {formatDateTime(signalRun.generated_at)}
                </span>
              ) : null}
              {signalRunError ? (
                <span style={{ fontSize: 11, color: "var(--tp-bad)" }}>
                  {signalRunError}
                </span>
              ) : null}
              <button
                type="button"
                className="tp-btn tp-btn-primary"
                onClick={() => void handleRunCryptoSignals()}
                disabled={signalRunning}
              >
                {signalRunning ? "Running..." : "Run crypto signals"}
              </button>
            </span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            {signalRun === null ? (
              <div style={{ color: "var(--tp-fg-3)", padding: 14 }}>
                Generate ranked crypto signals from watchlist heuristics,
                registered strategies, pair filters, research context, AI
                scoring, and paper-trade learning. Qualifying rows queue
                approvals only; broker orders are not placed from this action.
              </div>
            ) : signalRun.candidates.length === 0 ? (
              <div style={{ color: "var(--tp-fg-3)", padding: 14 }}>
                No executable crypto setup matched this run. Watchlist data and
                completed daily bars loaded, but the current market did not meet
                the breakout, momentum, dip-reversal, range-recovery, or
                registered strategy rules.
              </div>
            ) : (
              <div className="tp-table-scroll">
                <table className="tp-table tp-crypto-signal-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Symbol</th>
                      <th>Strategy</th>
                      <th>Score</th>
                      <th>AI</th>
                      <th>Entry</th>
                      <th>Stop</th>
                      <th>Target</th>
                      <th>Status</th>
                      <th>Reason</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signalRun.candidates.map((candidate) => (
                      <tr key={`${candidate.rank}-${candidate.symbol}-${candidate.strategy_id}`}>
                        <td className="num">{candidate.rank}</td>
                        <td>{candidate.symbol}</td>
                        <td>{candidate.strategy_id}</td>
                        <td className="num">{candidate.final_score}</td>
                        <td className="num">
                          {candidate.ai_confidence ?? "n/a"}
                        </td>
                        <td className="num">
                          {candidate.entry ? formatMoney(candidate.entry) : "n/a"}
                        </td>
                        <td className="num">
                          {candidate.stop ? formatMoney(candidate.stop) : "n/a"}
                        </td>
                        <td className="num">
                          {candidate.target
                            ? formatMoney(candidate.target)
                            : "n/a"}
                        </td>
                        <td>
                          <span
                            className={
                              "tp-status " +
                              (candidate.gate_status === "queued"
                                ? "ok"
                                : candidate.gate_status === "blocked"
                                  ? "bad"
                                  : "warn")
                            }
                          >
                            {candidate.gate_status.replace("_", " ")}
                          </span>
                        </td>
                        <td>{candidate.gate_reason.replaceAll("_", " ")}</td>
                        <td>
                          {candidate.approval_id ? (
                            <button
                              type="button"
                              className="tp-btn tp-btn-sm"
                              onClick={() =>
                                onOpenApproval(candidate.approval_id ?? "")
                              }
                            >
                              Review
                            </button>
                          ) : (
                            <span style={{ color: "var(--tp-fg-4)" }}>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section
          className="tp-tile tp-crypto-watch"
          style={{ gridColumn: "1 / -1" }}
        >
          <div className="tp-tile-head">
            <span className="label">Watchlist input analysis</span>
            <span
              className={`tp-status ${
                watchlistStatus === "live" ? "ok" : "warn"
              }`}
            >
              {watchlistStatus}
            </span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            {watchlistRows.length === 0 ? (
              <div style={{ color: "var(--tp-fg-3)", padding: 14 }}>
                {watchlistError ??
                  (watchlistEmpty
                    ? "No active crypto universe rows returned."
                    : "Loading crypto watchlist analysis.")}
              </div>
            ) : (
              watchlistRows.slice(0, 10).map((row) => {
                const pair = splitPair(row.symbol);
                return (
                  <div
                    className={`cx-watch-row ${
                      row.blocked_reason ? "blocked" : ""
                    }`}
                    key={row.symbol}
                  >
                    <div className="top">
                      <span className="pair">
                        {pair.base}
                        <span className="quote">-{pair.quote}</span>
                      </span>
                      <span className="cx-inst spot">{row.instrument_type}</span>
                      <span className="cx-venue">AP</span>
                      <span className="grow" />
                      <span className="num">{formatPct(row.distance_pct)}</span>
                    </div>
                    <div className="cx-watch-metrics">
                      <span>
                        Last <strong>{formatMoney(row.last_price ?? "n/a")}</strong>
                      </span>
                      <span>
                        Trigger{" "}
                        <strong>{formatMoney(row.trigger_price ?? "n/a")}</strong>
                      </span>
                      <span>
                        Det <strong>{row.det_score ?? "n/a"}</strong>
                      </span>
                      <span>
                        Prox <strong>{row.proximity}/4</strong>
                      </span>
                    </div>
                    <div className="plan">{row.plan}</div>
                    <div className="tp-ai-block" style={{ marginTop: 6 }}>
                      <div className="head">
                        <span>{row.regime_label ?? "analysis"}</span>
                        <span>{row.depth_state}</span>
                        {row.blocked_reason ? (
                          <span className="neg">{row.blocked_reason}</span>
                        ) : null}
                      </div>
                      <div className="quote">"{row.analysis}"</div>
                      <div className="cited">{row.cited}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Auto Paper Engine</span>
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {displayedEngineRun && !engineRunning ? (
                <span
                  style={{
                    fontSize: 11,
                    color:
                      displayedEngineRun.reason === "crypto_engine_busy"
                        ? "var(--tp-warn)"
                        : "var(--tp-good)",
                  }}
                >
                  {displayedEngineRun.status === "accepted"
                    ? "Run queued; refreshing status"
                    : displayedEngineRun.reason === "crypto_engine_busy"
                    ? "Engine already running; no new run started"
                    : `${displayedEngineRun.candidates_scored} scored · ${displayedEngineRun.approvals_created} approvals · ${displayedEngineRun.orders_placed} orders`}
                  {displayedEngineRun.reason &&
                  displayedEngineRun.reason !== "crypto_engine_busy" &&
                  displayedEngineRun.reason !== "crypto_engine_run_queued"
                    ? ` · ${displayedEngineRun.reason}`
                    : ""}
                </span>
              ) : null}
              {engineRunError && !engineRunning ? (
                <span style={{ fontSize: 11, color: "var(--tp-bad)" }}>
                  {engineRunError}
                </span>
              ) : null}
              <button
                type="button"
                className="tp-btn tp-btn-primary"
                onClick={() => void handleRunAutoPaperEngine()}
                disabled={engineRunning}
              >
                {engineRunning ? "Running..." : "Run auto paper engine"}
              </button>
              <span className="tp-status ok">
                {engineStatus?.auto_trading_enabled ? "enabled" : "disabled"}
              </span>
            </span>
          </div>
          <div className="tp-tile-body">
            <div className="tp-crypto-metrics">
              <Metric
                label="Adapters"
                value={String(engineStatus?.registered_crypto_paper_adapters ?? 0)}
                tone={
                  (engineStatus?.registered_crypto_paper_adapters ?? 0) > 0
                    ? "pos"
                    : "warn"
                }
              />
              <Metric
                label="Daily entries"
                value={`${engineStatus?.entries_today ?? 0}/${engineStatus?.max_daily_entries ?? 5}`}
              />
              <Metric
                label="Risk"
                value={`${engineStatus?.risk_per_trade_pct ?? "0.25"}%`}
              />
              <Metric
                label="Auto approvals"
                value={engineStatus?.auto_approval_enabled ? "On" : "Off"}
                tone={engineStatus?.auto_approval_enabled ? "pos" : "warn"}
              />
            </div>
            <div className="cx-note">
              Automation may queue, confirm, and place paper orders when enabled.
              Exits: protective stop with app-managed target.{" "}
              {engineStatus?.last_run
                ? `Last run: ${engineStatus.last_run.status}${
                    engineStatus.last_run.status === "ok"
                      ? ` (${engineStatus.last_run.candidates_scored} scored, ${engineStatus.last_run.approvals_created} approvals, ${engineStatus.last_run.orders_placed} orders)`
                      : engineStatus.last_run.reason
                        ? ` (${engineStatus.last_run.reason})`
                        : ""
                  }. `
                : ""}
              {engineStatus?.auto_trading_disabled_reason
                ? `Disabled: ${engineStatus.auto_trading_disabled_reason}. `
                : ""}
              {Object.entries(engineStatus?.exit_intents_by_status ?? {})
                .map(([k, v]) => `${k} ${v}`)
                .join(" · ") || "none"}
            </div>
          </div>
        </section>

        <section
          className="tp-tile"
          style={validationRuns.length > 0 ? { gridColumn: "1 / -1" } : undefined}
        >
          <div className="tp-tile-head">
            <span className="label">Strategy validation</span>
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {latestValidation ? (
                <span
                  className={`tp-status ${latestValidation.passed ? "ok" : "bad"}`}
                >
                  {latestValidation.status}
                </span>
              ) : null}
              {validationError ? (
                <span style={{ fontSize: 11, color: "var(--tp-bad)" }}>
                  {validationError}
                </span>
              ) : null}
              <button
                type="button"
                className="tp-btn"
                onClick={() => void refreshValidationRuns()}
                disabled={validationLoading}
              >
                {validationLoading ? "Refreshing..." : "Refresh"}
              </button>
            </span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            {validationRuns.length === 0 ? (
              <div style={{ color: "var(--tp-fg-3)", padding: 14 }}>
                No crypto strategy validation runs recorded yet. Signal
                generation can still run manually; validation history will
                expand here after the first run is recorded.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
                  gap: 0,
                }}
              >
                <div
                  style={{
                        borderRight: "1px solid var(--tp-border)",
                        borderBottom: "1px solid var(--tp-border)",
                    minWidth: 0,
                  }}
                >
                  {validationRuns.map((run) => (
                    <button
                      type="button"
                      key={run.id}
                      onClick={() => setSelectedValidationId(run.id)}
                      style={{
                        width: "100%",
                        border: 0,
                        borderBottom: "1px solid var(--tp-border)",
                        background:
                          selectedValidation?.id === run.id
                            ? "var(--tp-bg-2)"
                            : "transparent",
                        color: "var(--tp-fg-1)",
                        padding: "10px 12px",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 13,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {run.strategyId}
                        </span>
                        <span
                          style={{
                            color: run.passed
                              ? "var(--tp-good)"
                              : "var(--tp-bad)",
                            fontSize: 11,
                            flexShrink: 0,
                          }}
                        >
                          {run.status}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          color: "var(--tp-fg-3)",
                          fontSize: 11,
                          marginTop: 4,
                        }}
                      >
                        <span>{shortDate(run.createdAt)}</span>
                        <span className="num">
                          PF {run.baselineProfitFactor ?? run.dashboardSummary.profitFactor ?? "-"}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                {selectedValidation ? (
                  <div style={{ padding: 14, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "flex-start",
                        marginBottom: 12,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 15,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {selectedValidation.strategyId}
                        </div>
                        <div
                          style={{
                            color: "var(--tp-fg-3)",
                            fontSize: 12,
                            marginTop: 3,
                          }}
                        >
                          {shortDate(selectedValidation.periodStart)} to{" "}
                          {shortDate(selectedValidation.periodEnd)}
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ color: "var(--tp-fg-3)", fontSize: 11 }}>
                          Created
                        </div>
                        <div className="num" style={{ fontSize: 12 }}>
                          {formatDateTime(selectedValidation.createdAt)}
                        </div>
                      </div>
                    </div>

                    <div className="tp-crypto-metrics">
                      <Metric
                        label="Profit factor"
                        value={
                          selectedValidation.baselineProfitFactor ??
                          selectedValidation.dashboardSummary.profitFactor ??
                          "-"
                        }
                        tone={selectedValidation.passed ? "pos" : "warn"}
                      />
                      <Metric
                        label="Trades"
                        value={String(
                          selectedValidation.dashboardSummary.trades ??
                            selectedValidation.baselineTrades ??
                            0,
                        )}
                      />
                      <Metric
                        label="P&L"
                        value={
                          selectedValidation.dashboardSummary.totalPnl ??
                          selectedValidation.baselineTotalPnl ??
                          "0"
                        }
                      />
                      <Metric
                        label="Max DD"
                        value={selectedValidation.dashboardSummary.maxDrawdown ?? "-"}
                        tone="warn"
                      />
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(min(100%, 130px), 1fr))",
                        gap: 8,
                        marginTop: 12,
                      }}
                    >
                      <Metric
                        label="Lookahead"
                        value={checkLabel(selectedValidation.lookaheadPassed)}
                        tone={checkTone(selectedValidation.lookaheadPassed)}
                      />
                      <Metric
                        label="Recursive"
                        value={checkLabel(selectedValidation.recursiveStable)}
                        tone={checkTone(selectedValidation.recursiveStable)}
                      />
                      <Metric
                        label="Walk-forward"
                        value={checkLabel(selectedValidation.walkForwardPassed)}
                        tone={checkTone(selectedValidation.walkForwardPassed)}
                      />
                      <Metric
                        label="Sweep"
                        value={checkLabel(selectedValidation.parameterSweepPromoted)}
                        tone={checkTone(selectedValidation.parameterSweepPromoted)}
                      />
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(min(100%, 180px), 1fr))",
                        gap: 12,
                        marginTop: 14,
                      }}
                    >
                      <ValidationList
                        title="Pair locks"
                        empty="No pair locks"
                        rows={selectedValidation.pairLocks.map((lock) => ({
                          key: `${lock.symbol}-${lock.reason}-${lock.expiresAt ?? ""}`,
                          left: lock.symbol,
                          right: `${lock.reason} · ${shortDate(lock.expiresAt)}`,
                        }))}
                      />
                      <ValidationList
                        title="Protections"
                        empty="No protection triggers"
                        rows={selectedValidation.protectionTriggers.map((trigger) => ({
                          key: trigger.reason,
                          left: trigger.reason,
                          right: String(trigger.count),
                        }))}
                      />
                      <ValidationList
                        title="Attribution"
                        empty="No attribution"
                        rows={selectedValidation.strategyAttribution.map((row) => ({
                          key: row.strategyId,
                          left: row.strategyId,
                          right: `${row.trades} trades · PF ${row.profitFactor}`,
                        }))}
                      />
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                        gap: 12,
                        marginTop: 14,
                        borderTop: "1px solid var(--tp-border)",
                        paddingTop: 12,
                      }}
                    >
                      <ValidationList
                        title="Lookahead"
                        empty="No lookahead diagnostics"
                        rows={[
                          {
                            key: "reason",
                            left: selectedValidation.lookaheadDetails.reason ?? "ok",
                            right: `${selectedValidation.lookaheadDetails.baseline.signalsGenerated ?? 0} baseline signals`,
                          },
                          {
                            key: "entry-suppressed",
                            left: "entry suppressed",
                            right: `${selectedValidation.lookaheadDetails.entriesSuppressed.trades ?? 0} trades`,
                          },
                          {
                            key: "exit-suppressed",
                            left: "exit suppressed",
                            right: `${selectedValidation.lookaheadDetails.exitsSuppressed.signalsGenerated ?? 0} signals`,
                          },
                        ]}
                      />
                      <ValidationList
                        title="Recursive"
                        empty="No recursive diagnostics"
                        rows={selectedValidation.recursiveReports.map((row) => ({
                          key: String(row.startupCandleCount ?? "unknown"),
                          left: `${row.startupCandleCount ?? "n/a"} candles`,
                          right: `${row.trades ?? 0} trades · PF ${row.profitFactor ?? "-"}`,
                        }))}
                      />
                      <ValidationList
                        title="Walk-forward"
                        empty="No walk-forward windows"
                        rows={selectedValidation.walkForwardReports.map((row) => ({
                          key: `${row.validationStart ?? ""}-${row.validationEnd ?? ""}`,
                          left: `${shortDate(row.validationStart)} to ${shortDate(row.validationEnd)}`,
                          right: `${row.trades ?? 0} trades · PF ${row.profitFactor ?? "-"}`,
                        }))}
                      />
                      <ValidationList
                        title="Sweep trials"
                        empty="No sweep trials"
                        rows={selectedValidation.parameterSweepTrials.map(
                          (trial, index) => ({
                            key: `${index}-${JSON.stringify(trial.params)}`,
                            left: trial.promoted ? "promoted" : "trial",
                            right: `PF ${trial.validation.profitFactor ?? "-"} · ${formatParams(trial.params)}`,
                          }),
                        )}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </section>

        <section className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Pending approvals ({cryptoPending.length})</span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            {cryptoPending.length === 0 ? (
              <div style={{ color: "var(--tp-fg-3)", padding: 14 }}>
                No crypto approvals pending.
              </div>
            ) : (
              cryptoPending.map((approval) => {
                const pair = splitPair(approval.sym);
                const delta = approval.ai - approval.det;
                const diverge = Math.abs(delta) > 20;
                return (
                  <div className="cx-approval" key={approval.approvalId}>
                    <div className="top">
                      <span className="pair">
                        {pair.base}
                        <span className="quote">-{pair.quote}</span>
                      </span>
                      <span className="cx-inst spot">spot</span>
                      <span className="cx-side long">{approval.side}</span>
                      <span className="cx-venue">AP</span>
                      <span className="strat">· {approval.strategy}</span>
                      <span className="grow" />
                      <span className="tp-status warn">manual</span>
                    </div>
                    <div className="plan">
                      {formatMoney(approval.entry)}
                      <span style={{ color: "var(--tp-fg-4)" }}> · stop </span>
                      {formatMoney(approval.stop)}
                      <span style={{ color: "var(--tp-fg-4)" }}> · size </span>
                      {approval.qty}
                      <span style={{ color: "var(--tp-fg-4)" }}> · risk </span>
                      <span className="risk">{formatMoney(approval.risk)}</span>
                    </div>
                    <div className="tp-ai-block" style={{ marginTop: 6 }}>
                      <div className="head">
                        <span>AI rationale</span>
                        <span>conf {approval.ai}</span>
                        <span>· det {approval.det}</span>
                        <span className={diverge ? "neg" : "pos"}>
                          Δ{delta}
                        </span>
                      </div>
                      <div className="quote">"{approval.reason}"</div>
                      <div className="cited">{approval.cited}</div>
                    </div>
                    <div className="actions">
                      <button
                        type="button"
                        className="tp-btn tp-btn-primary"
                        onClick={() => onOpenApproval(approval.approvalId)}
                      >
                        Review
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="tp-tile" style={{ gridColumn: "1 / -1" }}>
          <div className="tp-tile-head">
            <span className="label">
              Crypto paper trade history ({tradeHistoryRows.length})
            </span>
            <span className="tp-tile-meta">closed trades</span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            {tradeHistoryRows.length === 0 ? (
              <div style={{ color: "var(--tp-fg-3)", padding: 14 }}>
                No closed crypto paper trades recorded yet.
              </div>
            ) : (
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Closed</th>
                    <th>Symbol</th>
                    <th>Strategy</th>
                    <th>Qty</th>
                    <th>Entry</th>
                    <th>Exit</th>
                    <th>P&L</th>
                    <th>R</th>
                    <th>Reason</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {tradeHistoryRows.map((trade) => (
                    <tr key={trade.intent_id}>
                      <td>{formatDateTime(trade.closed_at)}</td>
                      <td>{trade.symbol}</td>
                      <td>{trade.strategy_id}</td>
                      <td className="num">{trade.qty}</td>
                      <td className="num">{formatMoney(trade.entry_price)}</td>
                      <td className="num">{formatMoney(trade.exit_price)}</td>
                      <td
                        className={
                          Number(trade.realized_pnl) >= 0 ? "pos" : "neg"
                        }
                      >
                        {formatMoney(trade.realized_pnl)}
                      </td>
                      <td
                        className={Number(trade.outcome_r) >= 0 ? "pos" : "neg"}
                      >
                        {formatR(trade.outcome_r)}
                      </td>
                      <td>{trade.exit_reason}</td>
                      <td className="num">{trade.score ?? "n/a"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Crypto events</span>
            <span className="tp-tile-meta">{cryptoEvents.length}</span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            {cryptoEvents.length === 0 ? (
              <div style={{ color: "var(--tp-fg-3)", padding: 14 }}>
                No recent crypto events.
              </div>
            ) : (
              cryptoEvents.slice(0, 8).map((event, index) => (
                <div className="cx-fund-row" key={`${event.time}-${index}`}>
                  <span className="pair">{event.type}</span>
                  <span className="meta">{event.time}</span>
                  <span>{event.summary}</span>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

const Metric = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "pos" | "neg" | "warn";
}) => (
  <div className="tp-crypto-metric">
    <div>{label}</div>
    <strong className={tone === "warn" ? "flat" : tone}>{value}</strong>
  </div>
);

const ValidationList = ({
  title,
  empty,
  rows,
}: {
  title: string;
  empty: string;
  rows: Array<{ key: string; left: string; right: string }>;
}) => (
  <div style={{ minWidth: 0 }}>
    <div
      style={{
        color: "var(--tp-fg-3)",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: ".08em",
        marginBottom: 6,
      }}
    >
      {title}
    </div>
    {rows.length === 0 ? (
      <div style={{ color: "var(--tp-fg-3)", fontSize: 12 }}>{empty}</div>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {rows.slice(0, 5).map((row) => (
          <div
            key={row.key}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              fontSize: 12,
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontWeight: 600,
                color: "var(--tp-fg-1)",
                overflowWrap: "anywhere",
              }}
            >
              {row.left}
            </span>
            <span
              className="num"
              style={{
                color: "var(--tp-fg-3)",
                textAlign: "right",
                overflowWrap: "anywhere",
              }}
            >
              {row.right}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);
